import React, { useState, useEffect, useCallback } from 'react';
import chemicalService from '../../../services/chemicalService';
import SaleFormModal from '../components/SaleFormModal';
import SaleDetailsModal from '../components/SaleDetailsModal';

const PAGE_SIZE = 10;

const SalesTab = () => {
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({
    this_month_total: 0,
    this_month_count: 0,
    this_month_gst: 0,
    total_receivables: 0,
  });
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  const [showFormModal, setShowFormModal] = useState(false);
  const [detailsId, setDetailsId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, debouncedSearch, fromDate, toDate]);

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (statusFilter !== 'all') params.payment_status = statusFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const [listRes, summaryRes] = await Promise.all([
        chemicalService.getSales(params),
        chemicalService.getSalesSummary().catch((e) => {
          console.warn('Sales summary endpoint not available:', e.message);
          return { data: { this_month_total: 0, this_month_count: 0, this_month_gst: 0, total_receivables: 0 } };
        }),
      ]);

      setSales(listRes.data || []);
      setPagination(listRes.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
      setSummary(summaryRes.data || { this_month_total: 0, this_month_count: 0, this_month_gst: 0, total_receivables: 0 });
    } catch (err) {
      console.error('Error fetching sales:', err);
      alert('❌ ' + (err.response?.data?.message || 'Failed to load sales'));
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const handleDelete = async (s) => {
    if (!window.confirm(
      `Delete invoice "${s.invoice_no}" to ${s.customer_name}?\n\n` +
      `This will restore the sold stock back to inventory and reverse the customer's outstanding. Cannot be undone.`
    )) return;

    try {
      await chemicalService.deleteSale(s.id);
      alert('✅ Invoice deleted. Stock and customer balance restored.');
      if (sales.length === 1 && page > 1) {
        setPage(page - 1);
      } else {
        fetchSales();
      }
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || 'Failed to delete invoice'));
    }
  };

  const handleSaved = () => {
    setShowFormModal(false);
    fetchSales();
  };

  const clearDateFilters = () => {
    setFromDate('');
    setToDate('');
  };

  return (
    <div style={{ padding: '24px 30px 40px' }}>

      {/* ─── Summary Cards ───────────────────────── */}
      <div style={styles.summaryGrid}>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #2c3e50' }}>
          <div style={styles.summaryLabel}>This Month's Sales</div>
          <div style={{ ...styles.summaryValue, color: '#2c3e50' }}>
            {formatLakh(summary.this_month_total)}
          </div>
          <div style={styles.summarySub}>{summary.this_month_count} invoices</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #e74c3c' }}>
          <div style={styles.summaryLabel}>Outstanding Receivables</div>
          <div style={{ ...styles.summaryValue, color: '#e74c3c' }}>
            {formatLakh(summary.total_receivables)}
          </div>
          <div style={styles.summarySub}>from customers</div>
        </div>
        <div style={{ ...styles.summaryCard, borderLeft: '4px solid #27ae60' }}>
          <div style={styles.summaryLabel}>GST Output</div>
          <div style={{ ...styles.summaryValue, color: '#27ae60' }}>
            {formatLakh(summary.this_month_gst)}
          </div>
          <div style={styles.summarySub}>this month</div>
        </div>
      </div>

      {/* ─── Filters + Search + Add ──────────────── */}
      <div style={styles.toolbar}>
        <div style={styles.filterTabs}>
          {[
            { id: 'all', label: 'All' },
            { id: 'paid', label: 'Paid' },
            { id: 'partial', label: 'Partial' },
            { id: 'unpaid', label: 'Unpaid' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              style={{
                ...styles.filterButton,
                background: statusFilter === f.id ? '#e74c3c' : 'white',
                color: statusFilter === f.id ? 'white' : '#2c3e50',
                borderColor: statusFilter === f.id ? '#e74c3c' : '#e0e0e0',
              }}
            >
              {f.label}
            </button>
          ))}

          <div style={styles.dateRangeWrap}>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={styles.dateInput}
            />
            <span style={{ color: '#95a5a6', fontSize: '13px' }}>→</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={styles.dateInput}
            />
            {(fromDate || toDate) && (
              <button onClick={clearDateFilters} style={styles.clearButton}>✕</button>
            )}
          </div>
        </div>

        <div style={styles.toolbarRight}>
          <div style={styles.searchWrap}>
            <span style={styles.searchIcon}>⌕</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice no or customer..."
              style={styles.searchInput}
            />
          </div>
          <button onClick={() => setShowFormModal(true)} style={styles.addButton}>
            + New invoice
          </button>
        </div>
      </div>

      {/* ─── Sales Table ─────────────────────────── */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.headerRow}>
              <th style={styles.th}>Invoice No</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Customer</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Total</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Received</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Remaining</th>
              <th style={styles.th}>Status</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={styles.noData}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>⏳</div>
                  Loading sales...
                </td>
              </tr>
            ) : sales.length === 0 ? (
              <tr>
                <td colSpan="8" style={styles.noData}>
                  <div style={{ fontSize: '40px', marginBottom: '8px' }}>💼</div>
                  {debouncedSearch || statusFilter !== 'all' || fromDate || toDate
                    ? 'No invoices match your filters'
                    : 'No invoices yet — click "+ New invoice" to record one'}
                </td>
              </tr>
            ) : (
              sales.map((s) => {
                const grandTotal = parseFloat(s.grand_total) || 0;
                const amountPaid = parseFloat(s.amount_paid) || 0;
                const remaining = grandTotal - amountPaid;
                const isOverdue = s.due_date && new Date(s.due_date) < new Date() && remaining > 0;

                return (
                  <tr key={s.id} style={styles.row}>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '700', color: '#2c3e50' }}>{s.invoice_no}</div>
                      {isOverdue && (
                        <div style={styles.overdueTag}>⏱ Overdue</div>
                      )}
                    </td>
                    <td style={styles.td}>{formatDate(s.invoice_date)}</td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: '600', color: '#2c3e50' }}>{s.customer_name}</div>
                      {s.customer_ntn && (
                        <div style={{ fontSize: '11px', color: '#95a5a6', marginTop: '2px' }}>
                          NTN: {s.customer_ntn}
                        </div>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700' }}>
                      Rs {formatNumber(grandTotal)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#27ae60' }}>
                      Rs {formatNumber(amountPaid)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      {remaining > 0
                        ? <strong style={{ color: '#e74c3c' }}>Rs {formatNumber(remaining)}</strong>
                        : <span style={{ color: '#27ae60' }}>Rs 0</span>}
                    </td>
                    <td style={styles.td}>
                      <span style={getStatusBadge(s.payment_status)}>
                        {formatStatus(s.payment_status)}
                      </span>
                    </td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button onClick={() => setDetailsId(s.id)} style={styles.viewButton} title="View details">
                          👁️
                        </button>
                        <button onClick={() => handleDelete(s)} style={styles.deleteButton} title="Delete">
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Pagination ──────────────────────────── */}
      {!loading && pagination.total > 0 && (
        <div style={styles.paginationWrap}>
          <div style={styles.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}
            –{Math.min(pagination.page * pagination.limit, pagination.total)}
            {' '}of {pagination.total} invoices
          </div>
          <div style={styles.paginationButtons}>
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              «
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              style={{ ...styles.pageButton, ...(page === 1 ? styles.pageButtonDisabled : {}) }}
            >
              ‹ Prev
            </button>
            <span style={styles.pageIndicator}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              Next ›
            </button>
            <button
              onClick={() => setPage(pagination.totalPages)}
              disabled={page >= pagination.totalPages}
              style={{ ...styles.pageButton, ...(page >= pagination.totalPages ? styles.pageButtonDisabled : {}) }}
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* ─── Modals ─────────────────────────────── */}
      {showFormModal && (
        <SaleFormModal
          onClose={() => setShowFormModal(false)}
          onSaved={handleSaved}
        />
      )}

      {detailsId && (
        <SaleDetailsModal
          saleId={detailsId}
          onClose={() => setDetailsId(null)}
        />
      )}

    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatNumber = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatLakh = (n) => {
  const num = parseFloat(n) || 0;
  if (num >= 100000) return `Rs ${(num / 100000).toFixed(1)}L`;
  return `Rs ${formatNumber(num)}`;
};

const formatStatus = (status) => {
  const map = { paid: '✓ Paid', partial: 'Partial', unpaid: 'Unpaid' };
  return map[status] || status;
};

const getStatusBadge = (status) => {
  const palette = {
    paid:    { bg: '#d4edda', fg: '#155724' },
    partial: { bg: '#fff3cd', fg: '#856404' },
    unpaid:  { bg: '#f8d7da', fg: '#721c24' },
  };
  const c = palette[status] || palette.unpaid;
  return {
    background: c.bg, color: c.fg,
    padding: '4px 12px', borderRadius: '12px',
    fontSize: '12px', fontWeight: '600',
    display: 'inline-block',
  };
};

// ─── Styles ───────────────────────────────────────────────

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  summaryCard: {
    background: 'white',
    padding: '18px 22px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  },
  summaryLabel: { fontSize: '13px', color: '#7f8c8d', marginBottom: '6px', fontWeight: '600' },
  summaryValue: { fontSize: '28px', fontWeight: '700' },
  summarySub: { fontSize: '11px', color: '#95a5a6', marginTop: '4px' },

  toolbar: {
    background: 'white',
    padding: '16px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', flexWrap: 'wrap', gap: '16px',
    justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px',
  },
  filterTabs: { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  filterButton: {
    padding: '8px 16px',
    border: '2px solid', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', transition: 'all 0.2s',
  },
  dateRangeWrap: {
    display: 'flex', alignItems: 'center', gap: '6px',
    paddingLeft: '12px', marginLeft: '4px',
    borderLeft: '1px solid #e0e0e0',
  },
  dateInput: {
    padding: '8px 10px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '12px', fontFamily: 'inherit',
  },
  clearButton: {
    padding: '4px 10px',
    background: '#f0f0f0', color: '#7f8c8d',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '700',
  },
  toolbarRight: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  searchWrap: { position: 'relative' },
  searchIcon: {
    position: 'absolute', left: '12px', top: '50%',
    transform: 'translateY(-50%)',
    color: '#95a5a6', fontSize: '16px',
  },
  searchInput: {
    padding: '10px 12px 10px 36px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', width: '260px',
    fontFamily: 'inherit',
  },
  addButton: {
    padding: '10px 20px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },

  tableWrap: {
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  headerRow: { background: '#f8f9fa' },
  th: {
    padding: '14px 16px', textAlign: 'left',
    fontSize: '13px', fontWeight: '700',
    color: '#2c3e50', borderBottom: '2px solid #e0e0e0',
    whiteSpace: 'nowrap',
  },
  row: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#2c3e50' },
  noData: {
    padding: '60px 20px', textAlign: 'center',
    color: '#95a5a6', fontSize: '14px',
  },

  overdueTag: {
    display: 'inline-block',
    fontSize: '10px', color: 'white',
    background: '#e74c3c', padding: '2px 8px',
    borderRadius: '8px', marginTop: '4px',
    fontWeight: '700', letterSpacing: '0.3px',
  },

  viewButton: {
    padding: '6px 10px',
    background: '#3498db', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },
  deleteButton: {
    padding: '6px 10px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontSize: '14px',
  },

  paginationWrap: {
    marginTop: '16px',
    background: 'white',
    padding: '14px 20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', flexWrap: 'wrap', gap: '12px',
  },
  paginationInfo: { fontSize: '13px', color: '#7f8c8d' },
  paginationButtons: { display: 'flex', gap: '6px', alignItems: 'center' },
  pageButton: {
    padding: '8px 14px',
    background: 'white', color: '#2c3e50',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  },
  pageButtonDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  pageIndicator: {
    padding: '0 12px', fontSize: '13px',
    color: '#2c3e50', fontWeight: '600',
  },
};

export default SalesTab;